import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, RefreshCw, ArrowLeftRight, Compass, ArrowUp, ArrowDown, Minus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchMacroBrief, fetchMacro } from "@/lib/api";
import { FreshnessLine } from "@/components/cot/FreshnessLine";

const ECONOMIES = [
  { code: "US", label: "US · United States" },
  { code: "EU", label: "EU · Euro Area" },
  { code: "UK", label: "UK · United Kingdom" },
  { code: "JP", label: "JP · Japan" },
];

const IND_META = [
  { key: "consumer_confidence", label: "Cons. Confidence" },
  { key: "business_pmi", label: "Bus. Climate · PMI" },
  { key: "unemployment", label: "Unemployment" },
  { key: "payrolls", label: "NFP · MoM" },
];

function IndCell({ m }) {
  if (!m) return <span className="text-zinc-700 tabular-nums">--</span>;
  const good = m.higher_is_better ? m.change > 0 : m.change < 0;
  const bad = m.higher_is_better ? m.change < 0 : m.change > 0;
  const cls = m.change == null ? "text-zinc-600" : good ? "text-pos" : bad ? "text-neg" : "text-zinc-600";
  const Arrow = m.change > 0 ? ArrowUp : m.change < 0 ? ArrowDown : Minus;
  const unit = m.unit === "%" ? "%" : m.unit === "k" ? "k" : "";
  return (
    <span className="inline-flex items-baseline justify-end gap-1 tabular-nums">
      <span className="text-zinc-100">{m.value}{unit}</span>
      <span className={`inline-flex items-center text-[11px] ${cls}`}>
        <Arrow size={9} strokeWidth={2.5} />
      </span>
    </span>
  );
}

function MacroCompare({ codes }) {
  const [a, b] = codes;
  const qa = useQuery({ queryKey: ["macro", a], queryFn: () => fetchMacro(a), staleTime: 1000 * 60 * 60, retry: false });
  const qb = useQuery({ queryKey: ["macro", b], queryFn: () => fetchMacro(b), staleTime: 1000 * 60 * 60, retry: false });
  const ia = qa.data?.indicators || {};
  const ib = qb.data?.indicators || {};
  const rows = IND_META.filter((r) => ia[r.key] || ib[r.key]);
  if (!rows.length) return null;
  return (
    <div data-testid="brief-macro-compare">
      <div className="grid grid-cols-[1fr_3.25rem_3.25rem] gap-3 pb-1.5 mb-0.5 border-b border-zinc-800/60 text-[11px] uppercase tracking-widest text-zinc-600">
        <span>Indicator</span>
        <span className="text-right text-amber-term">{a}</span>
        <span className="text-right text-amber-term">{b}</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.key}
          className="grid grid-cols-[1fr_3.25rem_3.25rem] gap-3 py-[7px] text-[11px] border-b border-zinc-800/30 last:border-0 hover:bg-zinc-900/40 transition-colors"
          data-testid={`brief-macro-row-${r.key}`}
        >
          <span className="text-zinc-500 truncate flex items-center">{r.label}</span>
          <span className="text-right"><IndCell m={ia[r.key]} /></span>
          <span className="text-right"><IndCell m={ib[r.key]} /></span>
        </div>
      ))}
    </div>
  );
}

function EconomySelect({ value, onChange, exclude, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-7 w-[180px] bg-black border-zinc-700 text-xs font-mono uppercase tracking-wider text-zinc-200 focus:ring-amber-500"
        data-testid={testid}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-black border-zinc-700 text-zinc-200 font-mono">
        {ECONOMIES.filter((e) => e.code !== exclude).map((e) => (
          <SelectItem
            key={e.code}
            value={e.code}
            className="text-xs uppercase tracking-wider focus:bg-zinc-800 focus:text-amber-term"
            data-testid={`${testid}-opt-${e.code.toLowerCase()}`}
          >
            {e.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function DailyMacroBrief({ embedded = false }) {
  const [a, setA] = useState("US");
  const [b, setB] = useState("EU");

  const handleA = (v) => {
    if (v === b) setB(a); // keep them distinct by swapping
    setA(v);
  };
  const handleB = (v) => {
    if (v === a) setA(b);
    setB(v);
  };

  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const q = useQuery({
    queryKey: ["macro-brief", a, b],
    queryFn: () => fetchMacroBrief(a, b),
    enabled: a !== b,
    staleTime: 1000 * 60 * 60,
    refetchInterval: 1000 * 60 * 60,
    refetchIntervalInBackground: true,
    retry: false,
  });

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await fetchMacroBrief(a, b, true);
      qc.setQueryData(["macro-brief", a, b], fresh);
    } catch (e) {
      /* keep prior */
    } finally {
      setRefreshing(false);
    }
  };
  const busy = q.isFetching || refreshing;

  return (
    <div
      className={`${embedded ? "" : "border border-amber-500/30 "}bg-gradient-to-r from-amber-950/10 to-transparent`}
      data-testid="daily-macro-brief"
    >
      <div className="px-3 py-2 flex items-center gap-3 flex-wrap border-b border-zinc-800/80">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-amber-term">
          <Compass size={12} strokeWidth={1.5} /> Daily Macro Brief
        </span>
        <div className="inline-flex items-center gap-2">
          <EconomySelect value={a} onChange={handleA} exclude={b} testid="brief-select-a" />
          <ArrowLeftRight size={12} className="text-zinc-600" />
          <EconomySelect value={b} onChange={handleB} exclude={a} testid="brief-select-b" />
        </div>
        {q.data?.pair && (
          <span
            className="text-xs font-semibold tabular-nums text-zinc-100 border border-zinc-700 px-2 py-0.5"
            data-testid="brief-pair"
          >
            {q.data.pair}
          </span>
        )}
        <button
          type="button"
          onClick={forceRefresh}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-amber-term disabled:opacity-40"
          data-testid="brief-refresh"
        >
          <RefreshCw size={11} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
          {busy ? "Scanning wires…" : "Refresh"}
        </button>
      </div>

      <div className="px-3 py-2.5">
        {q.isLoading || busy ? (
          <p className="text-xs text-zinc-500 animate-pulse" data-testid="brief-loading">
            Synthesizing {a}↔{b} cross-market narrative from CPI, news bias & COT positioning…
          </p>
        ) : q.isError ? (
          <p className="text-xs text-zinc-600" data-testid="brief-error">
            Macro brief unavailable right now. Try Refresh.
          </p>
        ) : (
          <div data-testid="brief-body">
            <p className="text-xs text-zinc-300 leading-7" data-testid="brief-text">
              {q.data?.brief || q.data?.reason}
            </p>
            {q.data?.lean && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500">Lean</span>
                <span
                  className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 border ${
                    q.data.lean_direction === "down"
                      ? "text-neg border-red-500/40"
                      : q.data.lean_direction === "up"
                      ? "text-pos border-emerald-500/40"
                      : "text-amber-term border-amber-500/40"
                  }`}
                  data-testid="brief-lean"
                >
                  {q.data.lean}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="mt-2 flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-600">
          <Sparkles size={10} strokeWidth={1.5} /> Claude Sonnet 4.5 · CPI × News Bias × CFTC COT
          {q.data?.generated_at && (
            <FreshnessLine
              generatedAt={q.data.generated_at}
              headlineCount={q.data.headlines?.length || 0}
              className="ml-auto"
            />
          )}
        </div>
      </div>
    </div>
  );
}
